/**
 * GeoDetector — Fetches visitor geo info from the Edge Function.
 * Results are cached in localStorage for 24 hours.
 */

export interface GeoResult {
  country: string | null;
  region: string | null;
  regulation: "gdpr" | "ccpa" | "lgpd" | "pipeda" | "pdpa" | "popia" | "none";
  requiresConsent: boolean;
}

const GEO_CACHE_KEY = "pb_geo";
const GEO_CACHE_TTL = 24 * 60 * 60 * 1000; // 24h

export class GeoDetector {
  private apiUrl: string;

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  async detect(): Promise<GeoResult> {
    // Try cache first
    try {
      const cached = localStorage.getItem(GEO_CACHE_KEY);
      if (cached) {
        const { data, expiresAt } = JSON.parse(cached) as { data: GeoResult; expiresAt: number };
        if (Date.now() < expiresAt) return data;
      }
    } catch {
      // Ignore storage errors
    }

    // Fetch from Edge Function
    try {
      const res = await fetch(`${this.apiUrl}/functions/v1/geo-detect`, {
        signal: AbortSignal.timeout(3000),
      });
      if (!res.ok) return this.defaultResult();
      const data = (await res.json()) as GeoResult;

      // Cache the result
      try {
        localStorage.setItem(
          GEO_CACHE_KEY,
          JSON.stringify({ data, expiresAt: Date.now() + GEO_CACHE_TTL })
        );
      } catch {
        // Ignore
      }

      return data;
    } catch {
      return this.defaultResult();
    }
  }

  private defaultResult(): GeoResult {
    // Default to GDPR (opt-in) for unknown locations — safest choice
    return {
      country: null,
      region: null,
      regulation: "gdpr",
      requiresConsent: true,
    };
  }
}
